import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAppSelector } from 'app/store/store';
import { useGetPortEventsDataMutation } from 'app/api/facade';
import { portEventsCallsColumns } from './PortEventsTableColumns';
import { t } from 'i18next';
import { throttle } from 'app/helpers';
import { Table, Pagination } from 'app/components';
import { useParams } from 'react-router-dom';
import { Typo } from 'app/components/Typo';
import { getI18n } from 'react-i18next';
import styles from './PortDetails.module.scss';
import { getDateRange } from '../../helpers';

export interface PortTargetEventsRequestI {
  filter: {
    interval: {
      from: string;
      to: string;
    };
    datetime?: string;
    type: string[];
  };
  order: {
    direction: 'ASC' | 'DESC' | 'default';
    by: 'EVENT_DATE_TIME';
  };
}
export const PortTargetEventTypeEnum = {
  LowerTheAnchor: 'LOWER_THE_ANCHOR',
  RaiseTheAnchor: 'RAISE_THE_ANCHOR',
};
export interface SortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: 'EVENT_DATE_TIME';
}
const initialSort: SortingInterface = {
  direction: 'DESC',
  by: 'EVENT_DATE_TIME',
};

export const PortEvents = () => {
  const { portKey } = useParams();
  const language = getI18n().language;

  const user = useAppSelector((state) => state.userSlice.user);
  const userConfig = useAppSelector((state) => state.userSlice.uiConfig);

  const { today, fromDate, new_fromDate, new_today } = getDateRange(
    1,
    userConfig && userConfig.callEventMaxDepth ? userConfig?.callEventMaxDepth : 'P720TH',
  );
  const [getPortEvents, { data: portEvents, ...portEventsRequest }] = useGetPortEventsDataMutation();
  const debounceGetTargetsHandler = useCallback(throttle(getPortEvents, 500), []);
  const [sorting, setSorting] = useState<SortingInterface>(initialSort);
  const body: PortTargetEventsRequestI = useMemo(
    () => ({
      filter: {
        interval: {
          from: fromDate,
          to: today,
        },
        datetime: `${new_fromDate} / ${new_today}`,
        type: [PortTargetEventTypeEnum.LowerTheAnchor, PortTargetEventTypeEnum.RaiseTheAnchor],
      },
      order: sorting.direction === 'default' ? initialSort : sorting,
    }),
    [sorting],
  );

  useEffect(() => {
    if (user && portKey) {
      setCurrentPage(1);
      debounceGetTargetsHandler({ body, key: portKey });
    }
  }, [sorting, userConfig]);

  useEffect(() => {
    if (user && portKey) {
      debounceGetTargetsHandler({ body, key: portKey });
    }
  }, [user, language, userConfig]);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const tableData = useMemo(
    () => portEvents?.results?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [portEvents?.results, currentPage, itemsPerPage],
  );
  const columns = useMemo(() => portEventsCallsColumns, []);

  return (
    <>
      <div className={styles.table_header}>
        <Typo value={`${t('PortEvents')}:`} size="m" />
        <div className={styles.table_actions}></div>
      </div>
      {!portEventsRequest.isLoading && portEvents?.results?.length === 0 && (
        <p className={styles.no_data}>{t('NoResultsFound')}</p>
      )}
      {portEvents?.results && portEvents.results.length > 0 && tableData ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={tableData} setSorting={setSorting} />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={portEvents.results.length}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      ) : null}
      {portEventsRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}
    </>
  );
};
